import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AccountService, Reservation, UserCoupon, UserProfile, SavedPassenger, SavedPassengerRequest, InvoiceProfile, InvoiceProfileRequest } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';

type ReservationTab = 'active' | 'past';
type CouponTab = 'active' | 'used' | 'expired';
type View = 'reservations' | 'coupons' | 'profile' | 'passengers' | 'invoices';

@Component({
  selector: 'app-account',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.css']
})
export class AccountComponent implements OnInit {
  view: View = 'reservations';

  // Rezervasyonlar
  activeTab: ReservationTab = 'active';
  active: Reservation[] = [];
  past: Reservation[] = [];
  loading = true;
  error: string | null = null;

  // Kuponlar
  couponTab: CouponTab = 'active';
  activeCoupons: UserCoupon[] = [];
  usedCoupons: UserCoupon[] = [];
  expiredCoupons: UserCoupon[] = [];
  couponsLoading = false;
  couponsError: string | null = null;

  redeemCode = '';
  redeemSubmitting = false;
  redeemMessage: string | null = null;
  redeemSuccess = false;

  displayName = '';
  initial = 'U';

  // Profil
  profile: UserProfile | null = null;
  profileLoading = false;
  profileForm = { firstName: '', lastName: '', phoneNumber: '', email: '' };
  profileSaving = false;
  profileMessage: string | null = null;
  profileMessageSuccess = false;

  // Kayıtlı Kişiler
  passengers: SavedPassenger[] = [];
  passengersLoading = false;
  showPassengerForm = false;
  editingPassengerId: number | null = null;
  passengerForm: SavedPassengerRequest = this.emptyPassenger();
  passengerSaving = false;
  passengerMessage: string | null = null;
  passengerMessageSuccess = false;

  // Fatura Profilleri
  invoices: InvoiceProfile[] = [];
  invoicesLoading = false;
  showInvoiceForm = false;
  editingInvoiceId: number | null = null;
  invoiceForm: InvoiceProfileRequest = this.emptyInvoice();
  invoiceSaving = false;
  invoiceMessage: string | null = null;

  constructor(
    private accountService: AccountService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/login']);
      return;
    }
    this.displayName = this.authService.getUserDisplayName();
    this.initial = (this.displayName.trim().charAt(0) || 'U').toUpperCase();
    this.load();
  }

  setView(view: View): void {
    this.view = view;
    if (view === 'coupons' && this.activeCoupons.length === 0 && this.usedCoupons.length === 0 && this.expiredCoupons.length === 0) {
      this.loadCoupons();
    }
    if (view === 'profile' && !this.profile) {
      this.loadProfile();
    }
    if (view === 'passengers' && this.passengers.length === 0) {
      this.loadPassengers();
    }
    if (view === 'invoices' && this.invoices.length === 0) {
      this.loadInvoices();
    }
  }

  emptyInvoice(): InvoiceProfileRequest {
    return {
      type: 'Corporate', title: '', isDefault: false,
      tcNumber: '', personalName: '', personalSurname: '',
      companyName: '', taxOffice: '', taxNumber: '',
      companyAddress: '', companyCity: '', companyPostalCode: '',
      companyPhoneNumber: '', companyEmail: ''
    };
  }

  loadInvoices(): void {
    this.invoicesLoading = true;
    this.accountService.getInvoiceProfiles().subscribe({
      next: (list) => { this.invoices = list; this.invoicesLoading = false; },
      error: () => { this.invoicesLoading = false; }
    });
  }

  openAddInvoice(): void {
    this.invoiceForm = this.emptyInvoice();
    this.editingInvoiceId = null;
    this.showInvoiceForm = true;
    this.invoiceMessage = null;
  }

  openEditInvoice(p: InvoiceProfile): void {
    this.invoiceForm = { ...this.emptyInvoice(), ...p };
    this.editingInvoiceId = p.id;
    this.showInvoiceForm = true;
    this.invoiceMessage = null;
  }

  cancelInvoiceForm(): void {
    this.showInvoiceForm = false;
    this.editingInvoiceId = null;
  }

  saveInvoice(): void {
    this.invoiceMessage = null;
    if (!this.invoiceForm.title.trim()) {
      this.invoiceMessage = 'Profil başlığı zorunlu.';
      return;
    }
    this.invoiceSaving = true;
    const obs = this.editingInvoiceId
      ? this.accountService.updateInvoiceProfile(this.editingInvoiceId, this.invoiceForm)
      : this.accountService.addInvoiceProfile(this.invoiceForm);
    obs.subscribe({
      next: () => {
        this.invoiceSaving = false;
        this.showInvoiceForm = false;
        this.editingInvoiceId = null;
        this.invoices = [];
        this.loadInvoices();
      },
      error: (err) => {
        this.invoiceSaving = false;
        this.invoiceMessage = err?.error?.message || 'Kaydedilemedi.';
      }
    });
  }

  deleteInvoice(p: InvoiceProfile): void {
    if (!confirm(`"${p.title}" silinsin mi?`)) return;
    this.accountService.deleteInvoiceProfile(p.id).subscribe({
      next: () => { this.invoices = this.invoices.filter(x => x.id !== p.id); }
    });
  }

  emptyPassenger(): SavedPassengerRequest {
    return {
      firstName: '', lastName: '', gender: '', tcNumber: '',
      isForeign: false, birthDate: '', phoneNumber: '', email: '', isSelf: false
    };
  }

  loadPassengers(): void {
    this.passengersLoading = true;
    this.accountService.getPassengers().subscribe({
      next: (list) => { this.passengers = list; this.passengersLoading = false; },
      error: () => { this.passengersLoading = false; }
    });
  }

  openAddPassenger(): void {
    this.passengerForm = this.emptyPassenger();
    this.editingPassengerId = null;
    this.showPassengerForm = true;
    this.passengerMessage = null;
  }

  openEditPassenger(p: SavedPassenger): void {
    this.passengerForm = {
      firstName: p.firstName,
      lastName: p.lastName,
      gender: p.gender || '',
      tcNumber: p.tcNumber || '',
      isForeign: p.isForeign,
      birthDate: p.birthDate ? p.birthDate.slice(0, 10) : '',
      phoneNumber: p.phoneNumber || '',
      email: p.email || '',
      isSelf: p.isSelf
    };
    this.editingPassengerId = p.id;
    this.showPassengerForm = true;
    this.passengerMessage = null;
  }

  cancelPassengerForm(): void {
    this.showPassengerForm = false;
    this.editingPassengerId = null;
  }

  savePassenger(): void {
    this.passengerMessage = null;
    if (!this.passengerForm.firstName.trim() || !this.passengerForm.lastName.trim()) {
      this.passengerMessageSuccess = false;
      this.passengerMessage = 'Ad ve soyad zorunlu.';
      return;
    }
    this.passengerSaving = true;
    const req = {
      ...this.passengerForm,
      birthDate: this.passengerForm.birthDate ? new Date(this.passengerForm.birthDate).toISOString() : undefined
    };
    const obs = this.editingPassengerId
      ? this.accountService.updatePassenger(this.editingPassengerId, req)
      : this.accountService.addPassenger(req);

    obs.subscribe({
      next: () => {
        this.passengerSaving = false;
        this.showPassengerForm = false;
        this.editingPassengerId = null;
        this.passengers = [];
        this.loadPassengers();
      },
      error: (err) => {
        this.passengerSaving = false;
        this.passengerMessageSuccess = false;
        this.passengerMessage = err?.error?.message || 'Kaydedilemedi.';
      }
    });
  }

  deletePassenger(p: SavedPassenger): void {
    if (!confirm(`${p.firstName} ${p.lastName} silinsin mi?`)) return;
    this.accountService.deletePassenger(p.id).subscribe({
      next: () => {
        this.passengers = this.passengers.filter(x => x.id !== p.id);
      }
    });
  }

  hasMissingInfo(p: SavedPassenger): boolean {
    return !p.tcNumber && !p.isForeign || !p.birthDate || !p.gender;
  }

  loadProfile(): void {
    this.profileLoading = true;
    this.accountService.getProfile().subscribe({
      next: (p) => {
        this.profile = p;
        this.profileForm = {
          firstName: p.firstName || '',
          lastName: p.lastName || '',
          phoneNumber: p.phoneNumber || '',
          email: p.email || ''
        };
        this.profileLoading = false;
      },
      error: () => {
        this.profileLoading = false;
      }
    });
  }

  saveProfile(): void {
    this.profileSaving = true;
    this.profileMessage = null;
    this.accountService.updateProfile({
      firstName: this.profileForm.firstName.trim(),
      lastName: this.profileForm.lastName.trim(),
      phoneNumber: this.profileForm.phoneNumber.trim()
    }).subscribe({
      next: (p) => {
        this.profile = p;
        this.profileSaving = false;
        this.profileMessageSuccess = true;
        this.profileMessage = 'Profil bilgileri güncellendi.';
        setTimeout(() => (this.profileMessage = null), 3000);
      },
      error: () => {
        this.profileSaving = false;
        this.profileMessageSuccess = false;
        this.profileMessage = 'Güncelleme başarısız.';
      }
    });
  }


  load(): void {
    this.loading = true;
    this.error = null;
    this.accountService.getReservations().subscribe({
      next: (res) => {
        this.active = res.active || [];
        this.past = res.past || [];
        this.loading = false;
      },
      error: () => {
        this.error = 'Rezervasyonlar yüklenemedi.';
        this.loading = false;
      }
    });
  }

  loadCoupons(): void {
    this.couponsLoading = true;
    this.couponsError = null;
    this.accountService.getMyCoupons().subscribe({
      next: (res) => {
        this.activeCoupons = res.active || [];
        this.usedCoupons = res.used || [];
        this.expiredCoupons = res.expired || [];
        this.couponsLoading = false;
      },
      error: () => {
        this.couponsError = 'Kuponlar yüklenemedi.';
        this.couponsLoading = false;
      }
    });
  }

  setTab(tab: ReservationTab): void { this.activeTab = tab; }
  setCouponTab(tab: CouponTab): void { this.couponTab = tab; }

  redeem(): void {
    const code = this.redeemCode.trim();
    if (!code) return;
    this.redeemSubmitting = true;
    this.redeemMessage = null;
    this.accountService.redeemCoupon(code).subscribe({
      next: () => {
        this.redeemSubmitting = false;
        this.redeemSuccess = true;
        this.redeemMessage = 'Kupon hesabınıza eklendi!';
        this.redeemCode = '';
        this.loadCoupons();
        setTimeout(() => { this.redeemMessage = null; }, 3000);
      },
      error: (err) => {
        this.redeemSubmitting = false;
        this.redeemSuccess = false;
        this.redeemMessage = err?.error?.message || 'Kupon eklenemedi.';
      }
    });
  }

  statusLabel(status: string): string {
    const map: Record<string, string> = {
      Pending: 'Beklemede',
      Completed: 'Tamamlandı',
      Failed: 'Başarısız',
      Cancelled: 'İptal'
    };
    return map[status] ?? status;
  }

  formatDiscount(c: UserCoupon): string {
    if (c.coupon.discountType === 'Percentage') return `%${c.coupon.discountValue}`;
    return `${c.coupon.discountValue} ₺`;
  }

  currentCouponList(): UserCoupon[] {
    if (this.couponTab === 'active') return this.activeCoupons;
    if (this.couponTab === 'used') return this.usedCoupons;
    return this.expiredCoupons;
  }
}
